import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { ConnectionService } from "@services/connection-service";
import { BroadcastService } from "@services/broadcast-service";
import { EventLoggerService } from "@services/event-logger-service";
import { AppWebBridgeService } from "@services/app-web-bridge-service";
import { CurrentComponentService } from "@services/current-component";
import { SwiperOptions } from "swiper";
import { SwiperComponent } from "swiper/angular";
import { AppConfig } from "../../../app.config";
import { InstantCheckupService } from "@services/instant-checkup-service";
import { CaptureMedium, CapturePage, PhotoCaptureService } from "@services/photo-capture-service";
import { ExperimentService } from "@services/experiment";
import { UserService } from "@services/user";
import { getRebrandedHairPhotoCopy, QuizPhotoQuestionCopy } from "../../../consultation-quiz/quiz-photo-question-copy";

@Component({
  selector: "app-checkup-photo-instruction",
  templateUrl: "./checkup-photo-instruction.component.html",
  styles: [":host {@apply tw-block tw-h-full tw-relative};"],
  standalone: false,
})
export class CheckupPhotoInstructionComponent implements OnInit, OnDestroy {
  private readonly conn: ConnectionService = inject(ConnectionService);
  public readonly appConfig: AppConfig = inject(AppConfig);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly appWebBridgeService: AppWebBridgeService = inject(AppWebBridgeService);
  private readonly currentComponent: CurrentComponentService = inject(CurrentComponentService);
  private readonly instantCheckupService: InstantCheckupService = inject(InstantCheckupService);
  private readonly photoCapture: PhotoCaptureService = inject(PhotoCaptureService);
  private readonly experimentService: ExperimentService = inject(ExperimentService);
  private readonly userService: UserService = inject(UserService);

  user: any;
  tag: string;
  @ViewChild("slider", { static: false })
  slider: SwiperComponent;
  subscriptions: Array<Subscription> = [];
  isFrontFacePhoto: boolean;
  isSideFacePhoto: boolean;
  isHairFrontPhoto: boolean;
  isHairTopPhoto: boolean;
  queryParams: { [key: string]: any };
  readonly isCarouselReachedEnd: WritableSignal<boolean> = signal(false);
  readonly isGalleryUploadEnabled: WritableSignal<boolean> = signal(false);
  readonly rebrandedHairCopy: WritableSignal<QuizPhotoQuestionCopy | undefined> = signal(undefined);
  readonly rebrandedInstructionImageUrl: WritableSignal<string> = signal("");
  readonly takeHairPhotoLabel: WritableSignal<string> = signal("");
  readonly cameraIconClass: string = "cs-icon-camera tw-text-500 tw-mt-1";
  carouselConfig: SwiperOptions = {
    slidesPerView: 1,
    navigation: { prevEl: "nonext", nextEl: "nonext" },
    autoplay: {
      delay: 2000,
    },
    pagination: {
      type: "bullets",
    },
    on: {
      reachEnd: (): void => {
        this.isCarouselReachedEnd.set(true);
      },
    },
  };

  constructor() {
    this.currentComponent.set(this);
  }

  async ngOnInit(): Promise<any> {
    const startTimer = performance.now();
    this.user = this.conn.getActingUser();
    this.appWebBridgeService.notifyWebLoginToken();
    this.subscriptions.push(
      this.route.queryParams.subscribe((params: any): void => {
        this.queryParams = params;
        this.tag =
          params.tag || this.appConfig.Shared.InstantCheckup.Type.FULL_FACE;
        this.tag = this.tag.toUpperCase();
      })
    );
    this.setInstantCheckupFlag();
    await this.setupRebrandedHairInstruction();
    const endTimer = performance.now();
    this.eventLogger.trackEvent("instant_photo_upload_screen", {
      timeInMillSec: endTimer - startTimer,
      userId: this.user.id,
    });
    this.isGalleryUploadEnabled.set(
      await this.instantCheckupService.isGalleryUploadEnabled(this.tag)
    );
  }

  private async setupRebrandedHairInstruction(): Promise<void> {
    await this.experimentService.experimentsLoaded;
    const content = getRebrandedHairPhotoCopy()[this.tag];
    if (!content || !this.experimentService.isRebrandedHairInstructionsEnabled()) return;
    const gender: string = this.userService.getUserGender();
    this.rebrandedInstructionImageUrl.set(this.instantCheckupService.getPhotoInstructionImageUrl(
      this.tag, gender, this.conn.currentWebSiteLanguage, true,
    ) ?? "");
    this.takeHairPhotoLabel.set(this.isHairTopPhoto
      ? $localize`:@@takeTopPhoto:Take Top photo`
      : $localize`:@@takeFrontPhoto:Take Front photo`);
    this.rebrandedHairCopy.set(gender === this.appConfig.Shared.Gender.MALE ? content.male : content.female);
  }

  onRebrandedInstructionImageError(): void {
    this.rebrandedInstructionImageUrl.set(
      this.instantCheckupService.getPhotoInstructionFallbackUrl(this.rebrandedInstructionImageUrl()),
    );
  }

  showTakePhotoCta(): boolean {
    return !!this.slider?.swiperRef?.isEnd || this.isCarouselReachedEnd();
  }

  showNextCta(): boolean {
    return !this.showTakePhotoCta();
  }

  showGalleryUploadCta(): boolean {
    return this.showTakePhotoCta() && this.isGalleryUploadEnabled();
  }

  showFaceNextCta(): boolean {
    return this.hasCarouselSlides && this.showNextCta();
  }

  showFaceContinueCta(): boolean {
    return !this.hasCarouselSlides || this.showTakePhotoCta();
  }

  private get hasCarouselSlides(): boolean {
    return !!this.slider?.swiperRef?.slides?.length;
  }

  setInstantCheckupFlag(): void {
    switch (this.tag) {
      case this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT: {
        this.isHairFrontPhoto = true;
        break;
      }
      case this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP: {
        this.isHairTopPhoto = true;
        break;
      }
      case this.appConfig.Shared.InstantCheckup.Type.SIDE_FACE:
      case this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE:
      case this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE: {
        this.isSideFacePhoto = true;
        break;
      }
      case this.appConfig.Shared.InstantCheckup.Type.FULL_FACE:
      case this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE: {
        this.isFrontFacePhoto = true;
        break;
      }
    }
  }

  takePhoto(): void {
    this.photoCapture.launch({ tag: this.tag, context: { ...this.queryParams } });
  }

  uploadFromGallery(): void {
    this.photoCapture.launch({
      tag: this.tag,
      context: { ...this.queryParams },
      medium: CaptureMedium.GALLERY,
      page: CapturePage.Instruction,
    });
  }

  back(): void {
    this.broadcast.broadcast("NAVIGATION_BACK");
  }

  ngOnDestroy(): void {
    this.currentComponent.remove(this);
    this.subscriptions.forEach((subscription: Subscription): void =>
      subscription.unsubscribe()
    );
  }
}
